/**
 * Since this file is used in Vanilla Extract CSS, please limit the
 * dependency tree to only the bare minimum.
 */
import { isNotUndefined } from "@faire/typescript";

/**
 * @deprecated use SpacerMultipliers instead
 */
export const SpacerMultipliersBase8 = {
  "0": 0,
  "1/4x": 0.25,
  "1/2x": 0.5,
  "3/4x": 0.75,
  "1x": 1,
  "2x": 2,
  "3x": 3,
  "4x": 4,
  "5x": 5,
  "6x": 6,
  "7x": 7,
  "8x": 8,
  "9x": 9,
  "10x": 10,
  "-1x": -1,
  "-2x": -2,
  "-3x": -3,
  "-4x": -4,
  "-5x": -5,
  "-6x": -6,
  "-7x": -7,
  "-8x": -8,
  "-9x": -9,
  "-10x": -10,
} as const;

export const SpacerMultipliers = {
  "0": 0,
  "1/2x": 0.5,
  "3/2x": 1.5,
  "1x": 1,
  "2x": 2,
  "3x": 3,
  "4x": 4,
  "5x": 5,
  "6x": 6,
  "7x": 7,
  "8x": 8,
  "9x": 9,
  "10x": 10,
  "11x": 11,
  "12x": 12,
  "13x": 13,
  "14x": 14,
  "15x": 15,
  "16x": 16,
  "17x": 17,
  "18x": 18,
  "19x": 19,
  "20x": 20,
  "-1/2x": -0.5,
  "-3/2x": -1.5,
  "-1x": -1,
  "-2x": -2,
  "-3x": -3,
  "-4x": -4,
  "-5x": -5,
  "-6x": -6,
  "-7x": -7,
  "-8x": -8,
  "-9x": -9,
  "-10x": -10,
  "-11x": -11,
  "-12x": -12,
  "-13x": -13,
  "-14x": -14,
  "-15x": -15,
  "-16x": -16,
  "-17x": -17,
  "-18x": -18,
  "-19x": -19,
  "-20x": -20,
} as const;

/**
 * @deprecated use SpacerMultipliersKey instead
 */
export type SpacerMultipliersKeyBase8 = keyof typeof SpacerMultipliersBase8;
export type SpacerMultipliersKey = keyof typeof SpacerMultipliers;

/**
 * @deprecated use SpacerValueOrKey instead
 */
export type SpacerValueOrKeyBase8 = number | SpacerMultipliersKeyBase8;
export type SpacerValueOrKey = number | SpacerMultipliersKey;

/**
 * Vanilla extract doesn't support Styled-Components theme provider.
 * Will hard code the default theme's spacing value here.
 *
 * We aren't actively using theme provider in our codebase, and we don't
 * change spacing value often. So, this should be fine.
 *
 * @deprecated use SPACING_BASE instead
 */
const SPACING_BASE_8 = 8;
const SPACING_BASE = 4;

/**
 * @deprecated use getSpacingValue() instead
 */
export const getSpacingValueBase8 = (
  valueOrKeyBase8: SpacerValueOrKeyBase8
): number => {
  if (valueOrKeyBase8 in SpacerMultipliersBase8) {
    return (
      SpacerMultipliersBase8[valueOrKeyBase8 as SpacerMultipliersKeyBase8] *
      SPACING_BASE_8
    );
  }
  return valueOrKeyBase8 as number;
};

export const getSpacingValue = (valueOrKey: SpacerValueOrKey): number => {
  if (valueOrKey in SpacerMultipliers) {
    return SpacerMultipliers[valueOrKey as SpacerMultipliersKey] * SPACING_BASE;
  }
  return valueOrKey as number;
};

/**
 * @deprecated use buildSpacingString() instead
 */
const buildSpacingStringBase8 = (
  getSpacingValue: (value: SpacerValueOrKeyBase8) => number,
  valueOrKeyArg1: SpacerValueOrKeyBase8,
  valueOrKeyArg2?: SpacerValueOrKeyBase8,
  valueOrKeyArg3?: SpacerValueOrKeyBase8,
  valueOrKeyArg4?: SpacerValueOrKeyBase8
): string => {
  const pixelsOrNothing = (value?: SpacerValueOrKeyBase8): string =>
    isNotUndefined(value) ? ` ${getSpacingValue(value)}px` : "";
  const value1 = pixelsOrNothing(valueOrKeyArg1).trim();
  const value2 = pixelsOrNothing(valueOrKeyArg2);
  const value3 = pixelsOrNothing(valueOrKeyArg3);
  const value4 = pixelsOrNothing(valueOrKeyArg4);
  return `${value1}${value2}${value3}${value4}`;
};

const buildSpacingString = (
  getSpacingValue: (value: SpacerValueOrKey) => number,
  valueOrKeyArg1: SpacerValueOrKey,
  valueOrKeyArg2?: SpacerValueOrKey,
  valueOrKeyArg3?: SpacerValueOrKey,
  valueOrKeyArg4?: SpacerValueOrKey
): string => {
  const pixelsOrNothing = (value?: SpacerValueOrKey): string =>
    isNotUndefined(value) ? ` ${getSpacingValue(value)}px` : "";
  const value1 = pixelsOrNothing(valueOrKeyArg1).trim();
  const value2 = pixelsOrNothing(valueOrKeyArg2);
  const value3 = pixelsOrNothing(valueOrKeyArg3);
  const value4 = pixelsOrNothing(valueOrKeyArg4);
  return `${value1}${value2}${value3}${value4}`;
};

/**
 * Returns spacing in px using the base-8 grid (8x per multiplier unit).
 * This function takes up to four params, returning a string composed by those.
 *
 * @deprecated prefer `getSpacing()` (base 4) instead of `getSpacingBase8()` (base 8) - matching Slate's new dimension tokens base
 *
 * @example
 * getSpacingBase8("2x", 0, "2x", "3x")
 * // => "16px 0px 16px 24px"
 *
 * @param valueOrKeyBase8 number or spacer value
 */
export const getSpacingBase8 = (
  valueOrKeyBase8Arg1: SpacerValueOrKeyBase8,
  valueOrKeyBase8Arg2?: SpacerValueOrKeyBase8,
  valueOrKeyBase8Arg3?: SpacerValueOrKeyBase8,
  valueOrKeyBase8Arg4?: SpacerValueOrKeyBase8
): string =>
  buildSpacingStringBase8(
    getSpacingValueBase8,
    valueOrKeyBase8Arg1,
    valueOrKeyBase8Arg2,
    valueOrKeyBase8Arg3,
    valueOrKeyBase8Arg4
  );

/**
 * Returns spacing in px using the base-4 grid (4px per multiplier unit).
 * This function takes up to four params, returning a string composed by those.
 *
 * @example
 * getSpacing("2x")
 * // => "8px"
 *
 * @example
 * getSpacing("2x", 0, "2x", "3x")
 * // => "8px 0px 8px 12px"
 *
 * @param valueOrKey number or spacer value
 */
export const getSpacing = (
  valueOrKeyArg1: SpacerValueOrKey,
  valueOrKeyArg2?: SpacerValueOrKey,
  valueOrKeyArg3?: SpacerValueOrKey,
  valueOrKeyArg4?: SpacerValueOrKey
): string =>
  buildSpacingString(
    getSpacingValue,
    valueOrKeyArg1,
    valueOrKeyArg2,
    valueOrKeyArg3,
    valueOrKeyArg4
  );
